import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import Chart from "react-apexcharts";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { buildTrackerDashboardStats } from "./trackerDashboardStats";
import {
  getInitials,
  isBmsMemberSource,
  loadSubmissionsFromApi,
} from "./submissionStorage";
import { coachAvatarColor } from "./AddCoachesPopup";
import { isLeadCaptureTracker } from "./trackerStorage";

const PRIMARY = "var(--portal-theme-primary, #04abbb)";
const PRIMARY_SOFT =
  "color-mix(in srgb, var(--portal-theme-primary) 12%, white)";
const BORDER = "#e2e8f0";
const MUTED = "#64748b";
const INK = "#0f172a";

const ACTIVITY_META = {
  active: { label: "Active", color: "#047857", bg: "rgba(16,185,129,0.12)" },
  cooling: { label: "Cooling", color: "#b45309", bg: "rgba(245,158,11,0.14)" },
  at_risk: { label: "Quiet", color: "#b91c1c", bg: "rgba(239,68,68,0.12)" },
  completed: { label: "Completed", color: "#0369a1", bg: "rgba(14,165,233,0.12)" },
  dropped: { label: "Dropped", color: "#6b7280", bg: "rgba(148,163,184,0.2)" },
  unknown: { label: "—", color: MUTED, bg: "#f1f5f9" },
};

const HEALTH_META = {
  healthy: { label: "On track", color: "#047857", bg: "rgba(16,185,129,0.12)" },
  watch: { label: "Watch", color: "#b45309", bg: "rgba(245,158,11,0.14)" },
  needs_attention: {
    label: "Needs attention",
    color: "#b91c1c",
    bg: "rgba(239,68,68,0.12)",
  },
};

const VERDICT_META = {
  up: {
    label: "Improving",
    color: "#047857",
    bg: "rgba(16,185,129,0.12)",
    Icon: TrendingUpIcon,
  },
  down: {
    label: "Declining",
    color: "#b91c1c",
    bg: "rgba(239,68,68,0.12)",
    Icon: TrendingDownIcon,
  },
  mixed: {
    label: "Mixed",
    color: "#b45309",
    bg: "rgba(245,158,11,0.14)",
    Icon: TrendingFlatIcon,
  },
  flat: {
    label: "Stable",
    color: MUTED,
    bg: "#f1f5f9",
    Icon: TrendingFlatIcon,
  },
  none: {
    label: "No measures",
    color: MUTED,
    bg: "#f1f5f9",
    Icon: TrendingFlatIcon,
  },
};

const StatCard = ({ label, value, hint, tone }) => (
  <Box
    sx={{
      flex: "1 1 120px",
      minWidth: 110,
      p: 1.75,
      borderRadius: "12px",
      border: `1px solid ${BORDER}`,
      borderLeft: tone ? `3px solid ${tone}` : `1px solid ${BORDER}`,
      bgcolor: "#fff",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    }}
  >
    <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: MUTED }}>
      {label}
    </Typography>
    <Typography
      sx={{
        mt: 0.5,
        fontSize: 24,
        fontWeight: 700,
        color: tone || INK,
        lineHeight: 1.15,
      }}
    >
      {value}
    </Typography>
    {hint ? (
      <Typography sx={{ mt: 0.5, fontSize: 11.5, color: MUTED }}>{hint}</Typography>
    ) : null}
  </Box>
);

const Panel = ({ title, subtitle, action, children, sx }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: "12px",
      border: `1px solid ${BORDER}`,
      bgcolor: "#fff",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      height: "100%",
      ...sx,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 1,
        mb: subtitle ? 0.25 : 1.5,
        flexShrink: 0,
      }}
    >
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: INK }}>
        {title}
      </Typography>
      {action}
    </Box>
    {subtitle ? (
      <Typography sx={{ fontSize: 12, color: MUTED, mb: 1.5, flexShrink: 0 }}>
        {subtitle}
      </Typography>
    ) : null}
    {children}
  </Box>
);

const ProgressBar = ({ value, color = PRIMARY }) => (
  <Box sx={{ minWidth: 72 }}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, color, mb: 0.25 }}>
      {value}%
    </Typography>
    <LinearProgress
      variant="determinate"
      value={Math.min(100, Math.max(0, Number(value) || 0))}
      sx={{
        height: 6,
        borderRadius: 99,
        bgcolor: "#f1f5f9",
        "& .MuiLinearProgress-bar": { borderRadius: 99, bgcolor: color },
      }}
    />
  </Box>
);

const RankBadge = ({ rank }) => (
  <Box
    sx={{
      width: 24,
      height: 24,
      borderRadius: "8px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 11,
      fontWeight: 800,
      bgcolor: rank <= 3 ? "rgba(4,171,187,0.12)" : "#f1f5f9",
      color: rank <= 3 ? "#0f766e" : MUTED,
      flexShrink: 0,
    }}
  >
    {rank}
  </Box>
);

const LeadPersonRow = ({ person: c, rank, onOpen }) => (
  <Box
    onClick={() => onOpen(c)}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      py: 1.1,
      borderBottom: `1px solid #f1f5f9`,
      cursor: "pointer",
      "&:last-child": { borderBottom: 0 },
      "&:hover": { bgcolor: "#f8fafc" },
      borderRadius: "8px",
      px: 0.5,
    }}
  >
    <RankBadge rank={rank} />
    <Avatar
      sx={{
        width: 34,
        height: 34,
        fontSize: 12,
        fontWeight: 700,
        bgcolor:
          isBmsMemberSource(c.source) ? "#8b5cf6" : "#04abbb",
      }}
    >
      {getInitials(c.name)}
    </Avatar>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 13.5,
          fontWeight: 700,
          color: INK,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {c.name}
      </Typography>
      <Typography sx={{ fontSize: 12, color: MUTED }}>
        {c.statusName || "Lead"}
        {c.lastAt ? ` · ${moment(c.lastAt).fromNow()}` : ""}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: 12, color: MUTED, flexShrink: 0 }}>
      {c.coachName || "Unassigned"}
    </Typography>
  </Box>
);

const PersonCard = ({ person: c, rank, onOpen, variant = "best" }) => {
  const activity = ACTIVITY_META[c.activity] || ACTIVITY_META.unknown;
  const verdict = VERDICT_META[c.performance?.verdict] || VERDICT_META.none;
  const VerdictIcon = verdict.Icon;
  const primary = c.performance?.primary;
  const quietTone = variant === "quiet";

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onOpen(c)}
      sx={{
        textAlign: "left",
        width: "100%",
        border: 0,
        borderBottom: `1px solid ${BORDER}`,
        borderRadius: 0,
        bgcolor: "transparent",
        px: 0.25,
        py: 1.15,
        cursor: "pointer",
        display: "flex",
        alignItems: "flex-start",
        gap: 1.15,
        transition: "background 0.15s",
        "&:last-of-type": { borderBottom: 0 },
        "&:hover": {
          bgcolor: quietTone ? "rgba(245,158,11,0.06)" : PRIMARY_SOFT,
        },
      }}
    >
      <RankBadge rank={rank} />
      <Avatar
        sx={{
          width: 36,
          height: 36,
          fontSize: 12,
          fontWeight: 700,
          bgcolor:
            isBmsMemberSource(c.source) ? "#8b5cf6" : "#04abbb",
          flexShrink: 0,
        }}
      >
        {getInitials(c.name)}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.75,
            mb: 0.25,
          }}
        >
          <Typography
            sx={{
              fontSize: 13.5,
              fontWeight: 700,
              color: INK,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {c.name}
          </Typography>
          {quietTone ? (
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 800,
                color: c.daysSince >= 21 ? "#b91c1c" : "#b45309",
                flexShrink: 0,
              }}
            >
              {c.daysSince}d quiet
            </Typography>
          ) : (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.3,
                px: 0.75,
                py: 0.25,
                borderRadius: "999px",
                bgcolor: verdict.bg,
                color: verdict.color,
                flexShrink: 0,
              }}
            >
              <VerdictIcon sx={{ fontSize: 13 }} />
              <Typography sx={{ fontSize: 10.5, fontWeight: 800 }}>
                {verdict.label}
              </Typography>
            </Box>
          )}
        </Box>
        <Typography sx={{ fontSize: 11.5, color: MUTED, mb: 0.55 }}>
          {c.coachName}
          {quietTone
            ? ` · last ${moment(c.lastAt).fromNow()}`
            : primary
              ? ` · ${primary.label} ${primary.currentText}`
              : ` · ${c.progress}% journey`}
        </Typography>
        {quietTone ? (
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
            <Chip
              size="small"
              label={activity.label}
              sx={{
                height: 20,
                fontWeight: 700,
                fontSize: 10.5,
                bgcolor: activity.bg,
                color: activity.color,
              }}
            />
            {c.overdueReviews > 0 ? (
              <Chip
                size="small"
                label="Review overdue"
                sx={{
                  height: 20,
                  fontWeight: 700,
                  fontSize: 10.5,
                  bgcolor: "rgba(239,68,68,0.12)",
                  color: "#b91c1c",
                }}
              />
            ) : null}
            {c.performance?.verdict === "down" ? (
              <Chip
                size="small"
                label="Declining"
                sx={{
                  height: 20,
                  fontWeight: 700,
                  fontSize: 10.5,
                  bgcolor: "rgba(239,68,68,0.12)",
                  color: "#b91c1c",
                }}
              />
            ) : null}
          </Box>
        ) : (
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: verdict.color }}>
            {primary
              ? `${primary.deltaText} vs baseline`
              : `${c.submissions} check-in${c.submissions === 1 ? "" : "s"} · ${c.progress}%`}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

/**
 * Tracker overview — journey: performers vs quiet; lead: funnel + recent leads.
 */
export default function TrackerDetailOverviewTab({ tracker }) {
  const navigate = useNavigate();
  const isLead = isLeadCaptureTracker(tracker);
  const trackerId = tracker?.id || tracker?._id;
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(Boolean(trackerId));

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!trackerId) {
        setSubmissions([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      const result = await loadSubmissionsFromApi(trackerId, { limit: 500 });
      if (!cancelled) {
        setSubmissions(result.rows || []);
        setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [trackerId]);

  const stats = useMemo(() => {
    if (!trackerId) return null;
    return buildTrackerDashboardStats({
      trackers: [tracker],
      submissions,
      trendDays: 14,
    });
  }, [tracker, trackerId, submissions]);

  const clients = stats?.clients || [];
  const kpis = stats?.kpis;
  const trend = stats?.trend;
  const coaches = stats?.coaches || [];
  const attention = stats?.attention || [];
  const activityMix = stats?.activityMix || {};

  const bestPerformers = useMemo(() => {
    const eligible = clients.filter(
      (c) =>
        c.activity !== "dropped" &&
        c.activity !== "left" &&
        (c.performance?.verdict === "up" ||
          c.performance?.verdict === "mixed" ||
          c.progress >= 40 ||
          c.submissions >= 2)
    );
    const pool = eligible.length
      ? eligible
      : clients.filter((c) => !c.dropped && !c.left);
    return [...pool]
      .sort((a, b) => {
        const rank = (c) => {
          const v = c.performance?.verdict;
          const vScore =
            v === "up" ? 50 : v === "mixed" ? 25 : v === "flat" ? 10 : 0;
          const deltaBoost = Math.abs(c.performance?.primary?.delta || 0);
          return (
            vScore +
            deltaBoost +
            (c.progress || 0) / 4 +
            (c.submissions || 0) * 2 -
            (c.daysSince || 0)
          );
        };
        return rank(b) - rank(a);
      })
      .slice(0, 6);
  }, [clients]);

  const quietPeople = useMemo(() => {
    return [...clients]
      .filter(
        (c) =>
          !c.finished &&
          !c.dropped &&
          !c.left &&
          (c.activity === "at_risk" ||
            c.activity === "cooling" ||
            c.daysSince >= 7 ||
            c.overdueReviews > 0)
      )
      .sort(
        (a, b) =>
          (b.daysSince || 0) - (a.daysSince || 0) ||
          (b.overdueReviews || 0) - (a.overdueReviews || 0) ||
          (a.performance?.verdict === "down" ? 0 : 1) -
            (b.performance?.verdict === "down" ? 0 : 1)
      )
      .slice(0, 6);
  }, [clients]);

  const topCoaches = useMemo(
    () =>
      coaches
        .filter((c) => c.id !== "unassigned")
        .slice()
        .sort(
          (a, b) =>
            b.reviewRate - a.reviewRate ||
            a.reviewsDue - b.reviewsDue ||
            b.people - a.people
        ),
    [coaches]
  );

  const leadStatusCounts = useMemo(() => {
    const counts = {};
    clients.forEach((c) => {
      const key = c.statusName || "Unknown";
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [clients]);

  const recentLeads = useMemo(
    () =>
      [...clients]
        .sort(
          (a, b) =>
            moment(b.lastAt || 0).valueOf() - moment(a.lastAt || 0).valueOf()
        )
        .slice(0, 6),
    [clients]
  );

  const needsFollowUp = useMemo(() => {
    return [...clients]
      .filter((c) => {
        const s = String(c.statusName || "").toLowerCase();
        return (
          s.includes("new") ||
          s === "submitted" ||
          (!c.finished &&
            !c.dropped &&
            !c.left &&
            (c.daysSince == null || c.daysSince <= 2))
        );
      })
      .filter((c) => {
        const s = String(c.statusName || "").toLowerCase();
        return !s.includes("convert") && !s.includes("lost") && !c.finished;
      })
      .sort(
        (a, b) =>
          moment(a.lastAt || 0).valueOf() - moment(b.lastAt || 0).valueOf()
      )
      .slice(0, 6);
  }, [clients]);

  const openPerson = (c) => {
    if (c.latestSubmissionId) {
      navigate(`/tracker-submissions/${c.latestSubmissionId}`);
      return;
    }
    navigate(
      `/tracker-submissions?tracker=${encodeURIComponent(tracker.id)}`
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={36} sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  if (!stats || !kpis) return null;

  const hasData = kpis.submissions > 0 || kpis.people > 0;

  if (!hasData) {
    return (
      <Panel title={isLead ? "No leads yet" : "No activity yet"}>
        <Typography sx={{ py: 3, textAlign: "center", color: MUTED, fontSize: 13 }}>
          {isLead
            ? "Share the public link — new leads will show up here with status and follow-up."
            : "Once people submit on this journey, you'll see who is improving or going quiet here."}
        </Typography>
      </Panel>
    );
  }

  if (isLead) {
    const converted =
      leadStatusCounts["Converted"] ||
      Object.entries(leadStatusCounts).reduce((sum, [name, n]) => {
        if (String(name).toLowerCase().includes("convert")) return sum + n;
        return sum;
      }, 0);
    const contacted =
      leadStatusCounts["Contacted"] ||
      Object.entries(leadStatusCounts).reduce((sum, [name, n]) => {
        if (String(name).toLowerCase().includes("contact")) return sum + n;
        return sum;
      }, 0);
    const newLeads =
      leadStatusCounts["New Lead"] ||
      Object.entries(leadStatusCounts).reduce((sum, [name, n]) => {
        if (String(name).toLowerCase().includes("new")) return sum + n;
        return sum;
      }, 0);

    return (
      <Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mb: 2 }}>
          <StatCard
            label="Leads"
            value={kpis.people}
            hint={`${kpis.submissions} form${kpis.submissions === 1 ? "" : "s"}`}
            tone={PRIMARY}
          />
          <StatCard
            label="New"
            value={newLeads}
            hint="Awaiting contact"
            tone={newLeads > 0 ? "#0369a1" : MUTED}
          />
          <StatCard
            label="Contacted"
            value={contacted}
            hint="In progress"
            tone="#b45309"
          />
          <StatCard
            label="Converted"
            value={converted}
            hint="Won"
            tone="#047857"
          />
          <StatCard
            label="Coaches"
            value={coaches.filter((c) => c.id !== "unassigned").length}
            hint="Assigned follow-up"
            tone={MUTED}
          />
        </Box>

        <div className="row align-items-stretch">
          <div className="col-lg-6 mt-2 d-flex">
            <Panel
              title="Needs follow-up"
              subtitle="New leads waiting for a call or reply"
              sx={{
                width: "100%",
                height: 360,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {needsFollowUp.length === 0 ? (
                <Typography
                  sx={{ py: 3, textAlign: "center", color: MUTED, fontSize: 13 }}
                >
                  Nothing waiting — all leads have been moved on.
                </Typography>
              ) : (
                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }}>
                  {needsFollowUp.map((c, i) => (
                    <LeadPersonRow
                      key={c.key}
                      person={c}
                      rank={i + 1}
                      onOpen={openPerson}
                    />
                  ))}
                </Box>
              )}
            </Panel>
          </div>
          <div className="col-lg-6 mt-2 d-flex">
            <Panel
              title="Recent leads"
              subtitle="Latest submissions"
              sx={{
                width: "100%",
                height: 360,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {recentLeads.length === 0 ? (
                <Typography
                  sx={{ py: 3, textAlign: "center", color: MUTED, fontSize: 13 }}
                >
                  No leads yet.
                </Typography>
              ) : (
                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }}>
                  {recentLeads.map((c, i) => (
                    <LeadPersonRow
                      key={c.key}
                      person={c}
                      rank={i + 1}
                      onOpen={openPerson}
                    />
                  ))}
                </Box>
              )}
            </Panel>
          </div>
        </div>

        <div className="row">
          <div className="col-lg-7 mt-3">
            <Panel title="Lead submissions" subtitle="Last 14 days">
              <Chart
                options={{
                  chart: {
                    type: "area",
                    toolbar: { show: false },
                    zoom: { enabled: false },
                    fontFamily: "inherit",
                  },
                  colors: ["#04abbb"],
                  dataLabels: { enabled: false },
                  stroke: { curve: "smooth", width: 2.5 },
                  fill: {
                    type: "gradient",
                    gradient: {
                      shadeIntensity: 0.35,
                      opacityFrom: 0.4,
                      opacityTo: 0.05,
                      stops: [0, 90, 100],
                    },
                  },
                  grid: { borderColor: "#f1f5f9", strokeDashArray: 3 },
                  xaxis: {
                    categories: trend?.labels || [],
                    labels: { style: { colors: MUTED, fontSize: "11px" } },
                    axisBorder: { show: false },
                    axisTicks: { show: false },
                  },
                  yaxis: {
                    labels: { style: { colors: MUTED, fontSize: "11px" } },
                    min: 0,
                    forceNiceScale: true,
                  },
                  tooltip: { theme: "light" },
                }}
                series={[
                  {
                    name: "Leads",
                    data: trend?.values || [],
                  },
                ]}
                type="area"
                height={220}
              />
            </Panel>
          </div>
          <div className="col-lg-5 mt-3">
            <Panel title="By status" subtitle="Lead pipeline">
              {Object.keys(leadStatusCounts).length === 0 ? (
                <Typography sx={{ color: MUTED, fontSize: 13 }}>
                  No statuses yet.
                </Typography>
              ) : (
                <Stack spacing={1.25}>
                  {Object.entries(leadStatusCounts)
                    .sort((a, b) => b[1] - a[1])
                    .map(([name, count]) => (
                      <Box
                        key={name}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Typography
                          sx={{ fontSize: 13.5, fontWeight: 600, color: INK }}
                        >
                          {name}
                        </Typography>
                        <Chip
                          size="small"
                          label={count}
                          sx={{
                            fontWeight: 700,
                            bgcolor: PRIMARY_SOFT,
                            color: PRIMARY,
                          }}
                        />
                      </Box>
                    ))}
                </Stack>
              )}
            </Panel>
          </div>
        </div>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mb: 2 }}>
        <StatCard
          label="People"
          value={kpis.people}
          hint={`${kpis.activeClients} active`}
          tone={PRIMARY}
        />
        <StatCard
          label="Improving"
          value={kpis.improvingPeople || 0}
          hint="vs baseline measures"
          tone="#047857"
        />
        <StatCard
          label="Declining"
          value={kpis.decliningPeople || 0}
          hint="needs coach focus"
          tone={kpis.decliningPeople > 0 ? "#b91c1c" : MUTED}
        />
        <StatCard
          label="Quiet"
          value={
            (activityMix.cooling || 0) + (activityMix.at_risk || 0)
          }
          hint="7+ days without check-in"
          tone={
            (activityMix.cooling || 0) + (activityMix.at_risk || 0) > 0
              ? "#b45309"
              : MUTED
          }
        />
        <StatCard
          label="Reviews due"
          value={kpis.reviewsDue}
          hint={`${kpis.reviewsOverdue} overdue`}
          tone={kpis.reviewsOverdue > 0 ? "#b91c1c" : "#b45309"}
        />
      </Box>

      {attention.length > 0 ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {attention.slice(0, 3).map((item) => {
            const tone =
              item.severity === "high"
                ? { border: "#fecaca", bg: "#fef2f2", color: "#b91c1c" }
                : { border: "#fde68a", bg: "#fffbeb", color: "#b45309" };
            return (
              <Box
                key={item.id}
                sx={{
                  flex: "1 1 200px",
                  p: 1.35,
                  borderRadius: "10px",
                  border: `1px solid ${tone.border}`,
                  bgcolor: tone.bg,
                }}
              >
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: tone.color }}>
                  {item.title}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: MUTED, mt: 0.25 }}>
                  {item.detail}
                </Typography>
              </Box>
            );
          })}
        </Box>
      ) : null}

      {/* Best 6 + Quiet 6 */}
      <div className="row align-items-stretch">
        <div className="col-lg-6 mt-2 d-flex">
          <Panel
            title="Best performers"
            subtitle="Top 6 people improving on this journey"
            sx={{
              width: "100%",
              height: 360,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {bestPerformers.length === 0 ? (
              <Typography sx={{ py: 3, textAlign: "center", color: MUTED, fontSize: 13 }}>
                No strong performers yet — more check-ins will fill this list.
              </Typography>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  pr: 0.5,
                }}
              >
                {bestPerformers.map((c, i) => (
                  <PersonCard
                    key={c.key}
                    person={c}
                    rank={i + 1}
                    onOpen={openPerson}
                    variant="best"
                  />
                ))}
              </Box>
            )}
          </Panel>
        </div>
        <div className="col-lg-6 mt-2 d-flex">
          <Panel
            title="Gone quiet"
            subtitle="Top 6 people who need a nudge"
            sx={{
              width: "100%",
              height: 360,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {quietPeople.length === 0 ? (
              <Typography sx={{ py: 3, textAlign: "center", color: MUTED, fontSize: 13 }}>
                Looking good — nobody has gone quiet.
              </Typography>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  pr: 0.5,
                }}
              >
                {quietPeople.map((c, i) => (
                  <PersonCard
                    key={c.key}
                    person={c}
                    rank={i + 1}
                    onOpen={openPerson}
                    variant="quiet"
                  />
                ))}
              </Box>
            )}
          </Panel>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-7 mt-3">
          <Panel title="Check-in activity" subtitle="Last 14 days">
            <Chart
              options={{
                chart: {
                  type: "area",
                  toolbar: { show: false },
                  zoom: { enabled: false },
                  fontFamily: "inherit",
                },
                colors: ["#04abbb"],
                dataLabels: { enabled: false },
                stroke: { curve: "smooth", width: 2.5 },
                fill: {
                  type: "gradient",
                  gradient: {
                    shadeIntensity: 0.35,
                    opacityFrom: 0.4,
                    opacityTo: 0.05,
                    stops: [0, 90, 100],
                  },
                },
                grid: { borderColor: "#f1f5f9", strokeDashArray: 3 },
                xaxis: {
                  categories: trend.labels,
                  labels: { style: { colors: MUTED, fontSize: "11px" } },
                },
                yaxis: {
                  labels: { style: { colors: MUTED, fontSize: "11px" } },
                  min: 0,
                  forceNiceScale: true,
                },
                tooltip: { theme: "light" },
              }}
              series={[{ name: "Submissions", data: trend.counts }]}
              type="area"
              height={200}
            />
          </Panel>
        </div>
        <div className="col-lg-5 mt-3">
          <Panel title="People mix" subtitle="Engagement snapshot">
            <Chart
              options={{
                chart: {
                  type: "donut",
                  toolbar: { show: false },
                  fontFamily: "inherit",
                },
                labels: [
                  "Active",
                  "Cooling",
                  "Quiet",
                  "Completed",
                  "Dropped",
                  "Left",
                ],
                colors: [
                  "#10b981",
                  "#f59e0b",
                  "#ef4444",
                  "#0ea5e9",
                  "#94a3b8",
                  "#64748b",
                ],
                legend: { position: "bottom", fontSize: "11px" },
                dataLabels: { enabled: false },
                plotOptions: { pie: { donut: { size: "65%" } } },
              }}
              series={[
                activityMix.active,
                activityMix.cooling,
                activityMix.at_risk,
                activityMix.completed,
                activityMix.dropped,
                activityMix.left || 0,
              ]}
              type="donut"
              height={200}
            />
          </Panel>
        </div>
      </div>

      <div className="row">
        <div className="col-12 mt-3 mb-2">
          <Panel title="Coach leaderboard" subtitle="Review follow-through">
            {topCoaches.length === 0 ? (
              <Typography sx={{ py: 3, textAlign: "center", color: MUTED, fontSize: 13 }}>
                No coaches assigned yet.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: MUTED, fontSize: 11, width: 36 }}>
                      #
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: MUTED, fontSize: 11 }}>
                      Coach
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: MUTED, fontSize: 11 }}>
                      Health
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: MUTED, fontSize: 11 }}>
                      Review %
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: MUTED, fontSize: 11 }}>
                      Due
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topCoaches.slice(0, 6).map((c, i) => {
                    const meta = HEALTH_META[c.health] || HEALTH_META.watch;
                    return (
                      <TableRow key={`${c.id}-${i}`}>
                        <TableCell>
                          <RankBadge rank={i + 1} />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar
                              sx={{
                                width: 28,
                                height: 28,
                                fontSize: 11,
                                fontWeight: 700,
                                bgcolor: coachAvatarColor({
                                  id: c.id,
                                  first_name: c.name,
                                }),
                              }}
                            >
                              {getInitials(c.name)}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                                {c.name}
                              </Typography>
                              <Typography sx={{ fontSize: 11, color: MUTED }}>
                                {c.people} {c.people === 1 ? "person" : "people"}{" "}
                                · {c.submissions}{" "}
                                {c.submissions === 1 ? "entry" : "entries"}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={meta.label}
                            sx={{
                              height: 22,
                              fontWeight: 700,
                              fontSize: 11,
                              bgcolor: meta.bg,
                              color: meta.color,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <ProgressBar
                            value={c.reviewRate}
                            color={
                              c.reviewRate >= 70
                                ? "#10b981"
                                : c.reviewRate >= 40
                                  ? "#f59e0b"
                                  : "#ef4444"
                            }
                          />
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 700,
                            color: c.reviewsDue > 0 ? "#b91c1c" : "#047857",
                          }}
                        >
                          {c.reviewsDue}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Panel>
        </div>
      </div>
    </Box>
  );
}
